// ── Game Network Handlers ── Server message processing logic
// Extracted from Game class — each function takes a GameContext to access/mutate game state.

import * as THREE from 'three';
import { TICK_RATE, WEAPONS, type WeaponId, FIRE_RADIUS, type GrenadeId } from './core/config';
import { type ServerMessage } from './net/connection';
import { type PlayerSnapshot } from './net/messages';
import { type ScoreboardEntry } from './ui/screens/scoreboard';
import { type RoundPhase } from './ui/round-score';
import { RemotePlayer } from './gameplay/player';
import { attachTPPBomb, removeTPPBomb } from './gameplay/tpp-weapon';
import { triggerScreenShake, spawnMuzzleFlash } from './renderer/effects';
import { playHitConfirm, playBombPlant, playBombExplode, playBombDefused, playDistantGunfire } from './audio/sounds';
import { musicSystem } from './audio/music';
import { announcer, AnnouncerEvent } from './audio/announcer';
import { audioManager } from './audio/audio-manager';
import {
  pickRoundEndSting,
  resolveReloadVariant,
  playWaterSplash,
  playDecoyGunfire,
  playPainGrunt,
  playLowHPBeep,
  playLowAmmoBeep,
} from './audio/event-sounds';
import { skinRegistry } from './cosmetics/skin-registry';
import { type GameContext } from './game-types';
import { switchWeapon, updateMoneyDisplay } from './game-input-handlers';
import { resetLoadoutForTeam, setLoadoutSlot } from './gameplay/loadout';
import { isRegistered, saveRegistration, type RegistrationData } from './ui/registration-dialog';
import {
  spawnHEExplosion,
  spawnSmokeCloud,
  applyFlashEffect,
  spawnFireZone,
  createDecoyEffect,
} from './renderer/grenade-effects';
import {
  updateBombState,
  showPlantProgress,
  showDefuseProgress,
  hideProgress,
  hideBombHud,
  type BombState,
} from './ui/bomb-hud';
import { resetAimpunch, triggerAimpunch } from './gameplay/aimpunch';
import { applyTag } from './gameplay/player-controller';
import { voiceChat } from './audio/voice';
import { showEndScreen, type MatchData } from './ui/screens/end-screen';
import {
  onCommand as onPracticeCommand,
  updatePracticeHud,
  type PracticeStatus,
} from './ui/practice-hud';

// Bomb timing constants (must match server/internal/game/bomb.go)
const PLANT_TIME_S = 3.2;

// Track whether flash muffle is already active to prevent cascading volume reduction
let flashMuffleActive = false;

// Track round kills for announcer (FirstBlood, Ace)
let roundKillCount = 0;
let localPlayerRoundKills = 0;

// AUD-I2: Round timer threshold tracker — fire Countdown_1min/30sec exactly
// once when the round time crosses 60s/30s descending. Reset on round_start.
let lastRoundTimeRemaining: number | null = null;

// AUD-I2: Clutch tracker — fire Clutch at most once per round, when local team
// drops to exactly 1 alive vs >0 enemies. Reset on round_start.
let clutchAnnouncedThisRound = false;

// AUD-I2: EcoRound tracker — fire EcoRound at most once per round, when the
// freshly-started round has the local player below the eco threshold money.
// Reset on round_start.
let ecoAnnouncedThisRound = false;
const ECO_MONEY_THRESHOLD = 2000;
let localTeamStartedEcoThisRound = false;

// UI-M3: Threshold-tracker state for low-HP / low-ammo beeps. We fire each
// beep exactly once on the descending edge across the threshold so it
// doesn't repeat every tick. The beep retriggers after the player heals
// back above the threshold and then drops below again.
const LOW_HP_THRESHOLD = 20;
const HAPTIC_LOW_HEALTH_THRESHOLD = 25;
const LOW_AMMO_THRESHOLD = 5;
let lastLocalHealth: number | null = null;
let lastLocalAmmo: number | null = null;

/** Test/dispose hook — resets UI-M3 trackers (used by cleanupNetworkTimers). */
function resetLowResourceTrackers(): void {
  lastLocalHealth = null;
  lastLocalAmmo = null;
}

// AUD-I2: Site_Taken / Site_Lost announcers fire from the server's
// `site_captured` event (Slice K). Wired below in handleGameEvent.

// Track active setTimeout IDs so they can be cancelled on dispose
const activeTimers = new Set<ReturnType<typeof setTimeout>>();

// Track smoke grenade setTimeout IDs per grenade so repeated events don't leak timers
const smokeTimers = new Map<string, ReturnType<typeof setTimeout>[]>();

/** Clear all pending network handler timers and reset module-level state. */
export function cleanupNetworkTimers(): void {
  for (const id of activeTimers) clearTimeout(id);
  activeTimers.clear();
  for (const timers of smokeTimers.values()) {
    for (const id of timers) clearTimeout(id);
  }
  smokeTimers.clear();
  flashMuffleActive = false;
  roundKillCount = 0;
  localPlayerRoundKills = 0;
  lastRoundTimeRemaining = null;
  clutchAnnouncedThisRound = false;
  ecoAnnouncedThisRound = false;
  localTeamStartedEcoThisRound = false;
  resetLowResourceTrackers();
}
const DEFUSE_TIME_S = 10.0;
// F-GP-009 (audits/2026-05-07): `DEFUSE_WITH_KIT_TIME_S = 5.0` was removed —
// snapshots don't carry `hasKit`, so the defuse progress bar always uses the
// 10s denominator. If the server starts emitting `hasKit`, restore the
// constant and divide by `bomb.hasKit ? DEFUSE_WITH_KIT_TIME_S : DEFUSE_TIME_S`.
const BOMB_TIMER_S = 40.0;

type ReplicatedDoorState = {
  id: string;
  state: number;
  stateStart: number;
  animTime: number;
};

type DoorStateSink = {
  applyDoorStateSnapshots?: (snapshots: ReplicatedDoorState[], nowSeconds: number) => void;
};

/** Callbacks that the Game class provides for actions the network handlers need to trigger. */
export interface NetworkCallbacks {
  createLocalPlayerBody: () => void;
  toggleScope: () => void;
}

export function handleServerMessage(
  ctx: GameContext,
  msg: ServerMessage,
  callbacks: NetworkCallbacks,
): void {
  switch (msg.type) {
    case 'welcome':
      handleWelcome(ctx, msg.data, callbacks.createLocalPlayerBody);
      break;
    case 'snapshot':
      handleSnapshot(ctx, msg.data, callbacks.toggleScope);
      break;
    case 'event':
      handleEvent(ctx, msg.data);
      break;
    case 'voice_speaking':
      voiceChat.handleVoiceEvent(msg.type, msg.data);
      break;
    case 'world_state_rehydrate':
      // F-NC-002 — Server sends rehydrate as a top-level envelope, not wrapped
      // in `event`. Dispatch directly to handleGameEvent so late-joiners and
      // reconnecting players spawn the active smoke + fire zones.
      handleGameEvent(ctx, { type: 'world_state_rehydrate', data: msg.data });
      break;
    case 'error':
      console.error('Server error:', msg.data);
      break;
  }
}

/**
 * Protocol version this client was built against. Must match
 * `ProtocolVersion` in `server/internal/net/protocol.go`. F-SN-012: bump in
 * lockstep whenever the FlatBuffer / delta wire format changes so a stale
 * client disconnects before triggering an out-of-bounds slot read.
 */
export const CLIENT_PROTOCOL_VERSION = 9;

export function handleWelcome(
  ctx: GameContext,
  data: { playerId: number; name: string; reconnectToken?: string; protocolVersion?: number },
  createLocalPlayerBody: () => void,
): void {
  // F-SN-012: protocol-version handshake. Older servers (or older clients) won't
  // ship the field — log a warning but continue (best-effort backwards compat).
  // On a real mismatch, drop the connection so the user sees a clear error
  // instead of a half-decoded crash.
  if (typeof data.protocolVersion === 'number') {
    if (data.protocolVersion !== CLIENT_PROTOCOL_VERSION) {
      console.error(
        `[net] Protocol mismatch: server=${data.protocolVersion} client=${CLIENT_PROTOCOL_VERSION}. ` +
          `Disconnecting — please refresh the page.`,
      );
      try { ctx.connection.disconnect?.(); } catch { /* connection already closed */ }
      return;
    }
  } else {
    console.warn('[net] Server welcome omitted protocolVersion — pre-F-SN-012 server.');
  }

  // A-H-001: stop the main-menu drone before any in-game audio kicks in. Safe
  // no-op when nothing is playing; also covers the reconnect-mid-round path
  // where the menu music could otherwise leak indefinitely alongside gameplay.
  musicSystem.stopMenuMusic();

  ctx.localPlayer.id = data.playerId;
  ctx.localPlayer.name = data.name;

  // F-NC-003 — Capture the server-issued reconnect token. The server emits a
  // fresh token on every welcome and expects the client to echo it back as
  // `joinData.reconnectToken` on the next reconnect. Persist to
  // sessionStorage so a tab refresh inside the abandon grace window restores
  // the saved PlayerState instead of joining fresh.
  if (typeof data.reconnectToken === 'string' && data.reconnectToken.length > 0) {
    ctx.reconnectToken = data.reconnectToken;
    try {
      sessionStorage?.setItem('cp.reconnectToken', data.reconnectToken);
    } catch {
      /* sessionStorage may be unavailable in test contexts */
    }
  }
  // Overwatch panel was constructed with the placeholder id=0 before the
  // welcome arrived; sync the real player id so /api/overwatch/stats and the
  // case-load auth guard don't fail with "Reviewer ID not set". Optional
  // chained — voice-chat-only test contexts omit the panel.
  ctx.overwatchPanel?.setReviewerId(String(data.playerId));
  // Temporary position until first snapshot arrives with server-assigned spawn
  ctx.localPlayer.respawn(0, 0, 60);
  ctx.prediction.reset();

  // Show HUD and map elements
  ctx.hud.show();
  ctx.minimap.show();
  ctx.roundScore.show();
  ctx.roundScore.update(ctx.tScore, ctx.ctScore, ctx.roundNum, 30);
  updateMoneyDisplay(ctx);
  audioManager.play('round_start');
  // Create the local player's body mesh (visible when looking down)
  createLocalPlayerBody();

  // Wire voice chat to the WebRTC PeerConnection (created during welcome in connection.ts)
  const pc = ctx.connection.getPeerConnection();
  if (pc) {
    voiceChat.attachPeerConnection(pc);
  }

  // Practice HUD — indicator strip + command input. The server emits a
  // practice_status event after every ExecuteCommand (S9), but we still
  // populate an initial state + optimistic toggle so the UI feels instant.
  if (ctx.connection.getGameMode() === 'practice') {
    const practiceState: PracticeStatus = {
      godMode: false,
      noclip: false,
      infiniteMoney: true,
      botCount: 0,
    };
    updatePracticeHud(practiceState);
    onPracticeCommand((cmd, args) => {
      const fullCmd = args.length > 0 ? `/${cmd} ${args.join(' ')}` : `/${cmd}`;
      ctx.connection.send({ type: 'practice_command', data: { command: fullCmd } });
      if (cmd === 'god') practiceState.godMode = !practiceState.godMode;
      else if (cmd === 'noclip') practiceState.noclip = !practiceState.noclip;
      else if (cmd === 'bot_add') practiceState.botCount += 1;
      else if (cmd === 'bot_kick') practiceState.botCount = Math.max(0, practiceState.botCount - 1);
      updatePracticeHud(practiceState);
    });
  }

  // Check if player needs to register (first time)
  if (!isRegistered()) {
    // Show registration dialog first, then team select
    ctx.registrationDialog.show(
      async (regData: RegistrationData) => {
        // Try to register with server
        try {
          const httpUrl = ctx.serverUrl
            .replace('ws://', 'http://')
            .replace('wss://', 'https://')
            .replace('/ws', '/api/register');

          const res = await fetch(httpUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              username: regData.username,
              wallet_address: regData.walletAddress,
            }),
          });

          if (res.ok) {
            saveRegistration(regData);
            ctx.localPlayer.name = regData.username;
            ctx.registrationDialog.hide();
            // Now show team select
            ctx.state = 'team_select';
            ctx.touchInput?.setVisible(false);
            ctx.teamSelectMenu.show(15);
          } else {
            const err = await res.json().catch(() => ({ error: 'Registration failed' }));
            ctx.registrationDialog.showError(err.error ?? 'Registration failed');
          }
        } catch {
          // Server might not have DB — save locally and continue
          saveRegistration(regData);
          ctx.localPlayer.name = regData.username;
          ctx.registrationDialog.hide();
          ctx.state = 'team_select';
          ctx.touchInput?.setVisible(false);
          ctx.teamSelectMenu.show(15);
        }
      },
      () => {
        // Skip — go straight to team select
        ctx.state = 'team_select';
        ctx.touchInput?.setVisible(false);
        ctx.teamSelectMenu.show(15);
      },
    );
  } else {
    // Already registered — go straight to team select
    ctx.state = 'team_select';
    ctx.touchInput?.setVisible(false);
    ctx.teamSelectMenu.show(15);
  }
}

export function handleSnapshot(
  ctx: GameContext,
  data: any,
  toggleScopeFn: () => void,
): void {
  const tick = data.tick as number;
  const timeRemaining = data.timeRemaining as number;
  const players = data.players as Record<string, any>;
  const events = data.events as any[] | undefined;

  ctx.clock.updateServerTick(tick);
  if (timeRemaining !== undefined && !Number.isNaN(timeRemaining)) {
    ctx.hud.updateTimer(Math.floor(timeRemaining));

    // AUD-I2: Fire Countdown_1min / Countdown_30sec exactly once when the
    // round timer crosses each threshold descending. Cleared on round_start.
    if (lastRoundTimeRemaining !== null) {
      if (lastRoundTimeRemaining > 60 && timeRemaining <= 60) {
        announcer.announce(AnnouncerEvent.Countdown_1min);
      }
      if (lastRoundTimeRemaining > 30 && timeRemaining <= 30) {
        announcer.announce(AnnouncerEvent.Countdown_30sec);
      }
    }
    lastRoundTimeRemaining = timeRemaining;
  }

  if (Array.isArray(data.doorStates)) {
    const interactiveManager = ctx.scene?.userData?.interactiveManager as DoorStateSink | undefined;
    interactiveManager?.applyDoorStateSnapshots?.(data.doorStates, tick / TICK_RATE);
  }

  // Update money if server sends it
  if (data.money !== undefined) {
    ctx.money = data.money;
    updateMoneyDisplay(ctx);
    // BUG-W-9: keep an open buy menu in sync with round-end / kill bonuses.
    ctx.buyMenu.setMoney(ctx.money);
  }

  // Update round state if server sends it
  if (data.tScore !== undefined) {
    ctx.tScore = data.tScore;
    ctx.ctScore = data.ctScore ?? ctx.ctScore;
    ctx.roundNum = data.roundNumber ?? data.round ?? ctx.roundNum;
    ctx.roundScore.update(ctx.tScore, ctx.ctScore, ctx.roundNum, 30);
  }
  if (data.teamScores !== undefined) {
    // Server sends teamScores as array: [unused, T, CT]
    if (Array.isArray(data.teamScores)) {
      ctx.tScore = data.teamScores[1] ?? ctx.tScore;
      ctx.ctScore = data.teamScores[2] ?? ctx.ctScore;
    } else {
      ctx.tScore = data.teamScores.t ?? data.teamScores.T ?? ctx.tScore;
      ctx.ctScore = data.teamScores.ct ?? data.teamScores.CT ?? ctx.ctScore;
    }
    ctx.roundScore.update(ctx.tScore, ctx.ctScore, ctx.roundNum, 30);
  }
  if (data.roundNumber !== undefined && data.tScore === undefined) {
    ctx.roundNum = data.roundNumber;
    ctx.roundScore.update(ctx.tScore, ctx.ctScore, ctx.roundNum, 30);
  }

  // Update round phase if server sends it
  if (data.roundPhase !== undefined) {
    const phase = data.roundPhase as RoundPhase;
    if (phase !== ctx.roundPhase) {
      ctx.roundPhase = phase;
      ctx.roundScore.setPhase(phase);
      // Only allow buying during freeze time
      const buyAllowed = phase === 'freeze_time';
      ctx.buyMenu.setBuyAllowed(buyAllowed);
      // BUG-W-8: auto-close the buy menu when buy time ends so players see
      // the HUD instead of the "BUY TIME IS OVER" overlay frozen on screen.
      if (!buyAllowed && ctx.buyMenu.isOpen()) {
        ctx.buyMenu.hide();
      }
      // MOB-W6: show the quick-buy buttons only during freeze_time, mirror
      // the same gate as the buy menu so the buttons disappear the moment
      // the round goes active.
      ctx.touchInput?.getButtons()?.setQuickBuyVisible(buyAllowed);
    }
  }

  // Update team if server sends it
  if (data.team !== undefined) {
    const team = data.team === 'T' || data.team === 'CT' ? data.team : null;
    ctx.buyMenu.setTeam(team);
    // MOB-W6: keep the quick-buy controller's team in sync so unsaved
    // loadouts pick up the right per-team defaults; saved loadouts win.
    if (ctx.quickBuy && (team === 'T' || team === 'CT')) {
      ctx.quickBuy.setTeam(team);
      const buttons = ctx.touchInput?.getButtons();
      if (buttons) {
        const loadout = ctx.quickBuy.getLoadout();
        for (let i = 0; i < loadout.length; i++) {
          buttons.setQuickBuyLabel(i as 0 | 1 | 2, ctx.quickBuy.getButtonLabel(i as 0 | 1 | 2));
        }
      }
    }
  }

  // ── Bomb state from server ──
  if (data.bomb) {
    const bomb = data.bomb;
    const isLocalPlanter = bomb.planterId === ctx.localPlayer.id;
    const isLocalDefuser = bomb.defuserId === ctx.localPlayer.id;

    // Plant progress bar (only show for the local player who is planting)
    if (bomb.isPlanting && isLocalPlanter) {
      showPlantProgress(bomb.plantProgress / PLANT_TIME_S);
    // Defuse progress bar (only show for the local player who is defusing)
    } else if (bomb.isDefusing && isLocalDefuser) {
      // Server doesn't send hasKit directly. Default to the non-kit (10s) bar.
      // The inference based on progress was unreliable — always showing the
      // longer bar is safe and will complete early if the player has a kit.
      showDefuseProgress(bomb.defuseProgress / DEFUSE_TIME_S, false);
    } else {
      hideProgress();
    }

    // Bomb HUD state (planted banner, timer, carrier indicator)
    const carrierPlayerId = !bomb.isPlanted && bomb.planterId > 0
      ? String(bomb.planterId)
      : null;
    const bombHudState: BombState = {
      planted: bomb.isPlanted === true,
      site: bomb.plantSite === 'A' || bomb.plantSite === 'B' ? bomb.plantSite : null,
      timeRemaining: bomb.timer ?? 0,
      totalTime: BOMB_TIMER_S,
      carrierPlayerId,
      isLocalCarrier: carrierPlayerId !== null && bomb.planterId === ctx.localPlayer.id,
    };
    updateBombState(bombHudState);

    // Update 3D bomb entity timer state (controls blink rate)
    if (bomb.isPlanted && !bomb.isDetonated && !bomb.isDefused) {
      ctx.bombEntity.setTimerState(bomb.timer ?? 0, BOMB_TIMER_S);
    } else if (!bomb.isPlanted && bomb.planterId > 0) {
      // F-GP-008: bomb is being carried (server reports a planterId before
      // the plant completes). Hide the world-space 3D bomb so it doesn't
      // render at its last drop location while the carrier holds it; the
      // TPP body bomb prop drives the visible "who has the bomb" cue.
      ctx.bombEntity.setCarried();
    }
    musicSystem.updateAdaptiveState({
      bombTimeRemaining: bomb.timer ?? BOMB_TIMER_S,
      bombTotalTime: BOMB_TIMER_S,
      defuseActive: !!bomb.isDefusing,
    });

    // Hide bomb HUD + 3D model if bomb has detonated or been defused
    if (bomb.isDetonated || bomb.isDefused) {
      hideBombHud();
      ctx.bombEntity.group.visible = false;
    }
  } else {
    // No bomb data in snapshot — hide all bomb UI and 3D model
    hideProgress();
    hideBombHud();
    ctx.bombEntity.group.visible = false;
  }

  // ── Grenade state from server snapshots ──
  if (data.activeGrenades && Array.isArray(data.activeGrenades)) {
    ctx.grenadeProjectiles.update(0, data.activeGrenades);
  }

  // Process player states
  const seenIds = new Set<number>();

  for (const [idStr, pData] of Object.entries(players)) {
    const id = parseInt(idStr);
    seenIds.add(id);

    if (id === ctx.localPlayer.id) {
      const serverSnap: PlayerSnapshot = {
        id,
        position: pData.position,
        yaw: pData.yaw,
        pitch: pData.pitch,
        health: pData.health,
        armor: pData.armor,
        weapon: pData.weaponId,
        isCrouching: pData.isCrouching,
      };

      const reconciled = ctx.prediction.reconcile(
        {
          x: ctx.localPlayer.position.x,
          y: ctx.localPlayer.position.y,
          z: ctx.localPlayer.position.z,
          yaw: ctx.localPlayer.yaw,
          pitch: ctx.localPlayer.pitch,
          isCrouching: ctx.localPlayer.isCrouching,
          isGrounded: ctx.localPlayer.isGrounded,
          isSliding: ctx.localPlayer.isSliding,
        },
        serverSnap,
        pData.lastInputSeq ?? 0,
      );

      ctx.localPlayer.position.set(reconciled.x, reconciled.y, reconciled.z);
      ctx.localPlayer.isGrounded = reconciled.isGrounded;

      // UI-M3: Detect descending crossing of the low-HP threshold and fire
      // the warning beep exactly once per crossing. We compare against the
      // previous snapshot's value (lastLocalHealth) — equal-or-above last
      // tick + below this tick = rising edge into "low" state. Death
      // (health <= 0) and respawn (lastLocalHealth becomes null via
      // cleanupNetworkTimers if hooked, otherwise we tolerate the first
      // post-respawn tick by skipping when last is null).
      const prevHealth = lastLocalHealth;
      const newHealth = pData.health;
      if (
        typeof newHealth === 'number' &&
        newHealth > 0 &&
        prevHealth !== null &&
        prevHealth >= LOW_HP_THRESHOLD &&
        newHealth < LOW_HP_THRESHOLD
      ) {
        playLowHPBeep();
      }
      if (typeof newHealth === 'number' && prevHealth !== null) {
        if (prevHealth > 0 && newHealth <= 0) {
          ctx.hapticFeedback?.death?.();
        } else if (
          newHealth > 0 &&
          prevHealth >= HAPTIC_LOW_HEALTH_THRESHOLD &&
          newHealth < HAPTIC_LOW_HEALTH_THRESHOLD
        ) {
          ctx.hapticFeedback?.lowHealth?.();
        }
      }
      lastLocalHealth = typeof newHealth === 'number' ? newHealth : prevHealth;

      ctx.localPlayer.health = pData.health;
      ctx.localPlayer.armor = pData.armor;
      ctx.localPlayer.kills = pData.kills ?? ctx.localPlayer.kills;
      ctx.localPlayer.deaths = pData.deaths ?? ctx.localPlayer.deaths;
      if (pData.money !== undefined) {
        ctx.money = pData.money;
        updateMoneyDisplay(ctx);
        // BUG-W-9: keep an open buy menu in sync when the per-player snapshot
        // carries the money update (round-end bonuses, kill rewards, etc.).
        ctx.buyMenu.setMoney(ctx.money);
      }
      if (pData.team !== undefined) {
        const prevTeam = ctx.localPlayer.team;
        ctx.localPlayer.team = pData.team;
        ctx.buyMenu.setTeam(pData.team === 1 ? 'T' : pData.team === 2 ? 'CT' : null);
        // Seed/reseed the slot table whenever the team changes — including
        // the very first spectator(0) → T/CT transition that happens once
        // a fresh-joined player picks a side. Without this, loadoutSlots
        // stays at {primary:null, secondary:null, melee:'knife'} and the
        // `2` keybind silently fails because secondary is still null even
        // though the server actually equipped glock/usp. (Reported as
        // "switching to knife removed all my guns".)
        if (prevTeam !== pData.team) {
          resetLoadoutForTeam(ctx.loadoutSlots, pData.team);
        }
      }

      // Handle death
      if (!pData.isAlive && ctx.state === 'playing') {
        ctx.state = 'dead';
        ctx.touchInput?.setVisible(true);
        ctx.deathToSpectatorTimer = 0;
        ctx.deathScreen.show('Unknown', 'weapon', {
          onReplay: () => ctx.deathReplay.play(ctx.localPlayer.id),
          onSpectatorNav: (dir) => {
            if (dir === 'next') ctx.spectator.requestNext(ctx.connection);
            else ctx.spectator.requestPrev(ctx.connection);
          },
        });
        audioManager.play('death');
        // Screen shake on death
        triggerScreenShake(0.06, 0.3);
        // Unscope
        if (ctx.isScoped) toggleScopeFn();
        // F-RN-017: clear recoil-kick burst counter + spring momentum on death
        // so the next life starts from a settled spring (otherwise the killer's
        // last burst carries pitchSpring momentum into respawn).
        ctx.recoilKick.reset();
      } else if (pData.isAlive && (ctx.state === 'dead' || ctx.state === 'spectating')) {
        // Respawn — exit spectator mode
        ctx.state = 'playing';
        ctx.deathScreen.hide();
        ctx.killcamActive = false;
        ctx.killcamTarget = null;
        ctx.spectator.exit();
        ctx.spectatorHud.hide();
        ctx.hud.setSpectatorMode(false);
        ctx.scoreboard.setSpectating(false);
        ctx.hud.show();
        ctx.prediction.reset();
        // Disable X-ray when exiting spectator
        if (ctx.xrayOverlay?.isActive) {
          ctx.xrayOverlay.toggle();
        }
        // Reset aimpunch so camera flinch doesn't carry over from previous life
        resetAimpunch();
        // F-RN-017: respawn also clears recoil-kick — defensive, since death
        // already resets, but covers paths that skip the death event (e.g.
        // direct spectator → playing transition on round restart).
        ctx.recoilKick.reset();
        // Snap to server-assigned spawn position to avoid getting stuck
        ctx.localPlayer.respawn(
          pData.position.x,
          pData.position.y,
          pData.position.z,
        );
        // Server's Respawn() wipes OwnedWeapons back to {knife, team-pistol},
        // so reset the slot table to match — otherwise stale primary keeps
        // pointing at last round's purchase and slot-1 silently fails.
        resetLoadoutForTeam(ctx.loadoutSlots, ctx.localPlayer.team);
      }

      // Update spectate target from server
      if (pData.isSpectating && pData.spectateTarget && ctx.spectator.active) {
        ctx.spectator.setTarget(pData.spectateTarget);
      }

      // Sync weapon state from server
      if (pData.ammo !== undefined) {
        // UI-M3: Detect descending crossing of the low-ammo threshold for
        // the magazine ammo. Same edge logic as low-HP — first transition
        // fires the beep, subsequent low-state ticks stay silent until the
        // player reloads back above the threshold.
        const prevAmmo = lastLocalAmmo;
        const newAmmo = pData.ammo as number;
        if (
          prevAmmo !== null &&
          prevAmmo > LOW_AMMO_THRESHOLD &&
          newAmmo <= LOW_AMMO_THRESHOLD &&
          newAmmo > 0
        ) {
          playLowAmmoBeep();
        }
        lastLocalAmmo = newAmmo;

        ctx.weaponState = {
          ...ctx.weaponState,
          ammo: pData.ammo,
          reserveAmmo: pData.reserveAmmo ?? ctx.weaponState.reserveAmmo,
        };
      }

      // Mirror of H1 (remote-player path) for the local player. Server may
      // change our weapon authoritatively (Arms Race advance, weapon-drop
      // swap, reconnect-restore) and the snapshot is the only signal —
      // without this branch the viewmodel and slot table desync from server
      // truth. The server JSON tag is `weaponId`; the binary decoder emits
      // `weapon`; accept either.
      const serverWeaponId = (pData.weapon ?? pData.weaponId) as WeaponId | undefined;
      if (serverWeaponId && WEAPONS[serverWeaponId] && serverWeaponId !== ctx.weaponState.id) {
        switchWeapon(ctx, serverWeaponId);
        setLoadoutSlot(ctx.loadoutSlots, serverWeaponId);
      }

      // Sync grenade inventory from server (authoritative)
      // When server sends grenades, use them. When absent (empty inventory), reset to zero.
      if (pData.grenades) {
        // Server reported grenades — set known types, zero out any not reported
        for (const gId of Object.keys(ctx.grenadeInventory)) {
          ctx.grenadeInventory[gId] = pData.grenades[gId] ?? 0;
        }
        for (const [gId, count] of Object.entries(pData.grenades)) {
          ctx.grenadeInventory[gId] = count as number;
        }
        ctx.hud.updateGrenadeSelection(ctx.selectedGrenade, ctx.grenadeInventory);
      } else if (pData.grenades === undefined) {
        // Protocol v3: no grenades field means empty inventory (round reset)
        // Only reset if we previously had grenades to avoid constant HUD updates
        const hadGrenades = Object.values(ctx.grenadeInventory).some(c => c > 0);
        if (hadGrenades) {
          for (const gId of Object.keys(ctx.grenadeInventory)) {
            ctx.grenadeInventory[gId] = 0;
          }
          ctx.selectedGrenade = null;
          ctx.hud.updateGrenadeSelection(null, ctx.grenadeInventory);
        }
      }
      ctx.buyMenu.setGrenadeInventory(ctx.grenadeInventory);

      // GP-W3: forward server-authoritative ownedWeapons to the buy menu so
      // canBuy() rejects rebuy attempts for weapons we already own. When the
      // snapshot doesn't carry the field (legacy / pre-protocol-bump server)
      // pass undefined to disable the gate rather than clobbering with {}.
      ctx.buyMenu.setOwnedWeapons?.(pData.ownedWeapons ?? undefined);

      // G6: attach/detach C4 on the local player's TPP body so the carrier is
      // visible in third-person spectator + the killcam, matching the remote
      // path below. Tracked on ctx so we only toggle on state change.
      if (ctx.localPlayerBody) {
        const wantsBomb = pData.isBombCarrier === true && pData.isAlive;
        if (wantsBomb && !ctx.localTppBombAttached) {
          attachTPPBomb(ctx.localPlayerBody);
          ctx.localTppBombAttached = true;
        } else if (!wantsBomb && ctx.localTppBombAttached) {
          removeTPPBomb(ctx.localPlayerBody);
          ctx.localTppBombAttached = false;
        }
      }

      // F-MOB-H3 (bomb): drive the mobile BOMB plant button visibility from
      // the same isBombCarrier flag. The button reuses the `interact` (USE)
      // input, so a tap routes to the same E-equivalent server message as
      // the desktop bomb plant. Hidden whenever the player is not the
      // carrier (or is dead) so it never clutters the HUD outside its
      // window of relevance.
      const carrierVisible = pData.isBombCarrier === true && pData.isAlive === true;
      ctx.touchInput?.getButtons()?.setBombVisible(carrierVisible);
    } else {
      // Remote player
      let remote = ctx.remotePlayers.get(id);
      if (!remote) {
        remote = new RemotePlayer();
        remote.id = id;
        remote.name = pData.name ?? `Player ${id}`;
        // Use skin system: read skinId from server data, resolve (no team restriction)
        const rawSkinId = pData.skinId ?? 0;
        const skinDef = skinRegistry.resolveAny(rawSkinId);
        remote.createFromSkin(ctx.scene, skinDef.id, ctx.skinLoader, skinDef.fallbackColor, ctx.weaponSkinLoader);
        ctx.remotePlayers.set(id, remote);
      }

      remote.health = pData.health;
      remote.team = pData.team ?? 0;
      remote.isCrouching = pData.isCrouching ?? false;
      remote.isSliding = pData.isSliding ?? false;

      // F-GP-001 — Push v7 climb/mantle bits so ClimbAnimator runs the canned
      // ladder hand-over-hand and mantle lean overlays on this player's body.
      // Optional-chained for test contexts that mock RemotePlayer without the
      // animation surface.
      remote.setClimbState?.({
        isOnLadder: pData.isOnLadder,
        ladderId: pData.ladderId,
        isMantling: pData.isMantling,
        mantleT: pData.mantleT,
        mantleDest: pData.mantleDest,
        position: { x: pData.position.x, y: pData.position.y, z: pData.position.z },
      });

      // H1: Update remote player's third-person weapon when it changes
      const weaponSkinId = pData.weaponSkinId ?? 0;
      if (pData.weapon && (pData.weapon !== remote.currentWeapon || weaponSkinId !== remote.currentWeaponSkinId)) {
        remote.equipWeapon(pData.weapon as WeaponId, weaponSkinId);
      }

      if (typeof pData.reloading === 'boolean') {
        const wasReloading = (remote as any).__audioReloading === true;
        if (pData.reloading !== wasReloading && pData.isAlive !== false) {
          const phase = pData.reloading ? 'start' : 'end';
          audioManager.playAt(
            resolveReloadVariant((pData.weapon ?? remote.currentWeapon) as string | undefined, phase),
            { x: pData.position.x, y: pData.position.y, z: pData.position.z },
            0.45,
          );
        }
        (remote as any).__audioReloading = pData.reloading;
      }

      // G6: Render a C4 on the back of the assigned bomb carrier so enemies
      // can spot them. The server clears HasBomb on plant/death/round-reset.
      if (remote.mesh) {
        const wantsBomb = pData.isBombCarrier === true && pData.isAlive;
        if (wantsBomb && !remote.tppBombAttached) {
          attachTPPBomb(remote.mesh);
          remote.tppBombAttached = true;
        } else if (!wantsBomb && remote.tppBombAttached) {
          removeTPPBomb(remote.mesh);
          remote.tppBombAttached = false;
        }
      }

      remote.updateFromServer({
        x: pData.position.x,
        y: pData.position.y,
        z: pData.position.z,
        yaw: pData.yaw,
        pitch: pData.pitch,
        timestamp: performance.now(),
      });

      // Hide dead remote players and clear their interpolation buffer
      if (!pData.isAlive) {
        remote.clearBuffer();
      }
      if (remote.mesh) {
        remote.mesh.visible = pData.isAlive;
      }
    }
  }

  // Remove disconnected players
  for (const [id, remote] of ctx.remotePlayers) {
    if (!seenIds.has(id)) {
      remote.removeMesh(ctx.scene);
      ctx.remotePlayers.delete(id);
    }
  }

  // Update scoreboard
  const entries: ScoreboardEntry[] = [];
  for (const [idStr, pData] of Object.entries(players)) {
    entries.push({
      name: pData.name ?? `Player ${idStr}`,
      kills: pData.kills ?? 0,
      deaths: pData.deaths ?? 0,
      ping: parseInt(idStr) === ctx.localPlayer.id ? Math.round(ctx.connection.getPing()) : 0,
      isLocal: parseInt(idStr) === ctx.localPlayer.id,
    });
  }
  ctx.scoreboard.update(entries);

  // Update team counts for team select menu
  if (ctx.teamSelectMenu.isOpen()) {
    let tCount = 0;
    let ctCount = 0;
    for (const [, pData] of Object.entries(players)) {
      if (pData.team === 1) tCount++;
      else if (pData.team === 2) ctCount++;
    }
    ctx.teamSelectMenu.updateTeamCounts(tCount, ctCount, 5);
  }

  if (ctx.localPlayer.team !== 0) {
    let localAlive = ctx.localPlayer.health > 0 ? 1 : 0;
    let enemyAlive = 0;
    let localMoneyTotal = typeof ctx.money === 'number' ? ctx.money : 0;
    let localMoneyCount = 1;
    let enemyMoneyTotal = 0;
    let enemyMoneyCount = 0;

    for (const [idStr, pData] of Object.entries(players)) {
      if (parseInt(idStr) === ctx.localPlayer.id) continue;
      if (pData.team !== 1 && pData.team !== 2) continue;
      const alive = pData.isAlive !== false && (pData.health ?? 100) > 0;
      if (pData.team === ctx.localPlayer.team) {
        if (alive) localAlive++;
        if (typeof pData.money === 'number') {
          localMoneyTotal += pData.money;
          localMoneyCount++;
        }
      } else {
        if (alive) enemyAlive++;
        if (typeof pData.money === 'number') {
          enemyMoneyTotal += pData.money;
          enemyMoneyCount++;
        }
      }
    }

    musicSystem.updateAdaptiveState({
      localAlive,
      enemyAlive,
      localTeamEconomy: localMoneyTotal / Math.max(1, localMoneyCount),
      enemyTeamEconomy: enemyMoneyTotal / Math.max(1, enemyMoneyCount),
      recentKill: false,
    });
  }

  // Process events
  if (events) {
    for (const event of events) {
      handleGameEvent(ctx, event);
    }
  }
}

export function handleEvent(ctx: GameContext, data: any): void {
  handleGameEvent(ctx, data);
}

export function handleGameEvent(ctx: GameContext, event: { type: string; data: any }): void {
  // Track kill/damage events for auto-director scoring
  if ((event.type === 'kill' || event.type === 'damage') && ctx.recentEvents) {
    ctx.recentEvents.push({
      type: event.type,
      data: { ...event.data, time: Date.now() },
    });
  }

  switch (event.type) {
    case 'chat': {
      ctx.chatUI.addMessage({
        playerId: event.data.playerId,
        playerName: event.data.playerName ?? `Player ${event.data.playerId}`,
        channel: event.data.channel === 'all' ? 'all' : 'team',
        message: event.data.message ?? '',
        timestamp: event.data.timestamp ?? Date.now(),
        isRadio: event.data.isRadio === true,
      });
      if (event.data.isRadio) {
        audioManager.play('radio_beep');
      }
      break;
    }
    case 'kill': {
      const { killer, victim, weaponId, bodyPart, noscope } = event.data;
      const killerName = getPlayerName(ctx, killer);
      const victimName = getPlayerName(ctx, victim);
      const weaponName = WEAPONS[weaponId as WeaponId]?.name ?? weaponId;
      ctx.hud.addKillFeedEntry(killerName, victimName, weaponId, bodyPart === 'head', !!noscope);

      // Announcer: first blood (first kill of the round)
      roundKillCount++;
      if (roundKillCount === 1) {
        announcer.announce(AnnouncerEvent.FirstBlood);
      }
      musicSystem.updateAdaptiveState({ recentKill: true });

      if (killer === ctx.localPlayer.id) {
        localPlayerRoundKills++;
        // Announcer: ace (5 kills by local player in one round)
        if (localPlayerRoundKills === 5) {
          announcer.announce(AnnouncerEvent.Ace);
        }
        playHitConfirm(bodyPart === 'head');
        ctx.hud.showHitMarker(bodyPart === 'head');
        // Kill streak + headshot indicator
        ctx.killEffect?.showKill(bodyPart === 'head');
        if (bodyPart === 'head') ctx.headshotIndicator?.show();
        // MOB-C1: haptic feedback for kill (mobile only — null on desktop)
        if (bodyPart === 'head') {
          ctx.hapticFeedback?.headshot();
        } else {
          ctx.hapticFeedback?.killConfirmed();
        }
        // Kill reward money
        const killReward = WEAPONS[weaponId as WeaponId]?.killReward ?? 300;
        ctx.money += killReward;
        updateMoneyDisplay(ctx);

        // F-CM-007: increment StatTrak kill count for the killing weapon if the
        // equipped instance is StatTrak. Mutate the loadout in place so the count
        // persists across weapon switches; only animate the LED tick when the
        // killing weapon is the one currently rendered (otherwise the new value
        // is picked up on the next applyWeaponSkin call via refreshStatTrak).
        const stKills = ctx.weaponLoadout?.statTrakKills;
        if (stKills && typeof stKills[weaponId] === 'number' && stKills[weaponId] >= 0) {
          stKills[weaponId] = stKills[weaponId] + 1;
          if (ctx.viewmodel.getCurrentWeaponId?.() === weaponId) {
            ctx.viewmodel.statTrakKill();
          }
        }
      }

      if (victim === ctx.localPlayer.id) {
        audioManager.play('death');
        // F-UI-003: Reset kill streak banner when the local player dies.
        ctx.killEffect?.resetStreak();
        ctx.deathScreen.show(killerName, weaponName, {
          onReplay: () => ctx.deathReplay.play(ctx.localPlayer.id),
          onSpectatorNav: (dir) => {
            if (dir === 'next') ctx.spectator.requestNext(ctx.connection);
            else ctx.spectator.requestPrev(ctx.connection);
          },
        });
        // Trigger death replay system (slow-motion killer POV replay)
        const tick = ctx.clock.getEstimatedServerTick();
        ctx.deathReplay.onDeath(killer, tick);
        // Also activate basic killcam as camera fallback
        const killerRemote = ctx.remotePlayers.get(killer);
        if (killerRemote) {
          ctx.killcamActive = true;
          ctx.killcamTarget = killerRemote;
          ctx.killcamTimer = 0;
        }
      }

      // Spawn ragdoll for the victim. Remote players use their network mesh;
      // the local player uses the TPP body group (ctx.localPlayerBody) so the
      // auto-enabling spectator view shows the corpse ragdolling instead of
      // standing stock-still at the death point.
      if (ctx.ragdollRenderer) {
        let victimMesh: THREE.Group | null | undefined;
        let victimPos: THREE.Vector3 | undefined;
        if (victim === ctx.localPlayer.id) {
          victimMesh = ctx.localPlayerBody;
          victimPos = ctx.localPlayer.position.clone();
        } else {
          const victimRemote = ctx.remotePlayers.get(victim);
          victimMesh = victimRemote?.mesh;
          victimPos = victimRemote?.position.clone();
        }

        if (victimMesh && victimPos) {
          const killerPos = killer === ctx.localPlayer.id
            ? ctx.localPlayer.position
            : ctx.remotePlayers.get(killer)?.position;
          let impactDir = new THREE.Vector3(0, 0, 1);
          if (killerPos) {
            impactDir = victimPos.clone().sub(killerPos).normalize();
          }
          ctx.ragdollRenderer.spawnRagdoll(victimMesh, victimPos, impactDir);
        }
      }

      // Play distant gunfire sound when local player is not involved in the kill
      if (killer !== ctx.localPlayer.id && victim !== ctx.localPlayer.id) {
        playDistantGunfire();
      }

      // AUD-I2: Clutch detection — if our team has exactly 1 player alive
      // (the local player) and at least 1 enemy is alive, fire Clutch.
      // At most one Clutch announcement per round (reset on round_start).
      if (!clutchAnnouncedThisRound && ctx.localPlayer.team !== 0 && ctx.localPlayer.alive) {
        const localTeam = ctx.localPlayer.team;
        let teammatesAlive = 1; // local player
        let enemiesAlive = 0;
        for (const remote of ctx.remotePlayers.values()) {
          if (remote.health <= 0) continue;
          if (remote.team === localTeam) teammatesAlive++;
          else if (remote.team !== 0) enemiesAlive++;
        }
        if (teammatesAlive === 1 && enemiesAlive > 0) {
          announcer.announce(AnnouncerEvent.Clutch);
          clutchAnnouncedThisRound = true;
        }
      }
      break;
    }
    case 'damage': {
      const { attacker, victim, bodyPart, damage, weaponId } = event.data;
      if (attacker === ctx.localPlayer.id && victim !== ctx.localPlayer.id) {
        playHitConfirm(bodyPart === 'head');
        ctx.hud.showHitMarker(bodyPart === 'head');
        // MOB-C1: haptic feedback when we deal damage (mobile only — null on desktop)
        if (bodyPart === 'head') {
          ctx.hapticFeedback?.headshot();
        } else {
          ctx.hapticFeedback?.hitConfirm();
        }

        // Spawn damage number at remote player position. Thread the server's
        // `bodyPart` through as the optional `damageType` so head/body/leg
        // damage numbers render in their distinct colors (batch 13).
        const remote = ctx.remotePlayers.get(victim);
        if (remote && damage) {
          const damageType =
            bodyPart === 'head'
              ? 'headshot'
              : bodyPart === 'legs'
                ? 'leg'
                : 'body';
          ctx.damageNumbers.spawn(
            remote.position.clone().setY(remote.position.y + 1.8),
            damage,
            bodyPart === 'head',
            damageType,
          );
        }
      }

      // Directional damage indicator when WE take damage
      if (victim === ctx.localPlayer.id && attacker !== ctx.localPlayer.id) {
        const attackerRemote = ctx.remotePlayers.get(attacker);
        if (attackerRemote && ctx.directionalIndicator) {
          // Calculate direction from local player to attacker, relative to player yaw
          const dx = attackerRemote.position.x - ctx.localPlayer.position.x;
          const dz = attackerRemote.position.z - ctx.localPlayer.position.z;
          const worldAngle = Math.atan2(dx, -dz); // angle in world space
          const relativeAngle = worldAngle - ctx.localPlayer.yaw;
          ctx.directionalIndicator.showDamage(relativeAngle);
        }
        // Arc-style indicator — pinpoints the attacker angle + scales with damage
        if (attackerRemote && ctx.damageIndicator && damage) {
          ctx.damageIndicator.showDamage(
            attackerRemote.position,
            damage,
            ctx.localPlayer.position,
            ctx.localPlayer.yaw,
          );
        }
        // Screen shake when hit
        triggerScreenShake(0.04, 0.2);
        // Aimpunch — camera flinch on damage (reduced with helmet/armor)
        triggerAimpunch(ctx.localPlayer.armor > 0);
        // MOB-C1: haptic feedback when WE take damage (mobile only — null on desktop)
        ctx.hapticFeedback?.takeDamage();
        // F-GP-003: predict server tagging (hit slowdown) so prediction speed
        // matches the next snapshot. Server emits weaponId on every damage
        // event (see server/internal/game/ticker_input.go). Falls back to
        // 'rifle' when the weapon is unknown so we still apply some slowdown.
        const cfg = typeof weaponId === 'string' ? WEAPONS[weaponId as WeaponId] : undefined;
        applyTag(ctx.localPlayer, cfg?.type ?? 'rifle', ctx.localPlayer.armor > 0);
      }
      break;
    }
    case 'wallbang': {
      // Shooter-only indicator — skip when the local player isn't the shooter
      if (event.data.shooter !== ctx.localPlayer.id) break;
      if (ctx.wallbangEl) {
        ctx.wallbangEl.style.display = 'block';
        ctx.wallbangTimer = 1.5;
      }
      break;
    }
    case 'weapon_fire': {
      // Remote players only — local shooter already gets FX via their own
      // input tick (game.ts plays fire sound + muzzle flash at the viewmodel).
      const { playerId, weaponId, x, y, z } = event.data;
      if (playerId === ctx.localPlayer.id) break;
      if (typeof x !== 'number' || typeof y !== 'number' || typeof z !== 'number') break;
      const origin = new THREE.Vector3(x, y, z);
      audioManager.playAt(`${weaponId}_fire`, { x, y, z });
      spawnMuzzleFlash(origin);
      break;
    }
    case 'world_state_rehydrate': {
      // G12 — late-join/reconnect path: server pushes the authoritative list
      // of active smoke + fire zones once after welcome so we can spawn them
      // even though the player missed the originating grenade_explode event.
      const smokes = (event.data.smokes ?? []) as Array<{ x: number; y: number; z: number; radius?: number }>;
      const fires = (event.data.fires ?? []) as Array<{ x: number; y: number; z: number; radius?: number }>;
      const v = new THREE.Vector3();
      for (const s of smokes) {
        v.set(s.x, s.y, s.z);
        const handle = spawnSmokeCloud(v, ctx.scene);
        ctx.activeSmokeZones.push(handle);
      }
      for (const f of fires) {
        v.set(f.x, f.y, f.z);
        const handle = spawnFireZone(v, f.radius ?? FIRE_RADIUS, ctx.scene);
        ctx.activeFireZones.push(handle);
      }
      break;
    }
    case 'practice_status': {
      // Authoritative practice-mode state push from the server (S9). Replaces
      // the optimistic toggle in the practice command callback above.
      if (event.data.playerId !== ctx.localPlayer.id) break;
      const status: PracticeStatus = {
        godMode: !!event.data.godMode,
        noclip: !!event.data.noclip,
        infiniteMoney: event.data.infiniteMoney !== false,
        botCount: typeof event.data.botCount === 'number' ? event.data.botCount : 0,
      };
      updatePracticeHud(status);
      const result = typeof event.data.result === 'string' ? event.data.result : '';
      if (result) {
        ctx.chatUI.addMessage({
          playerId: 0,
          playerName: 'PRACTICE',
          channel: 'all',
          message: result,
          timestamp: Date.now(),
        });
      }
      break;
    }
    case 'team_switch_blocked': {
      // Server rejected a team_select — surface the reason in chat + kill feed
      // and ping the UI-error tone so the player knows the request failed.
      const message = typeof event.data.message === 'string'
        ? event.data.message
        : 'Team switch blocked';
      ctx.chatUI.addMessage({
        playerId: 0,
        playerName: 'SERVER',
        channel: 'all',
        message,
        timestamp: Date.now(),
      });
      ctx.hud.addEventFeedEntry('🚫', message, '#ff6666');
      audioManager.play('ui_error');
      break;
    }
    case 'roundEnd': {
      // Server sends winnerTeam as uint8 (1=T, 2=CT). Scores are authoritative via
      // snapshot scoreboard state — do NOT mutate ctx.tScore/ctx.ctScore here or the
      // HUD briefly shows +1 before the server value overwrites.
      const winnerTeam = event.data.winnerTeam;
      const winner = winnerTeam === 1 ? 'T' : 'CT';
      const localTeam = ctx.localPlayer.team; // 1=T, 2=CT
      const localScoreBeforeRound = localTeam === 1 ? ctx.tScore : ctx.ctScore;
      const enemyScoreBeforeRound = localTeam === 1 ? ctx.ctScore : ctx.tScore;
      let localAliveAtEnd = ctx.localPlayer.health > 0 ? 1 : 0;
      let enemyAliveAtEnd = 0;
      for (const remote of ctx.remotePlayers.values()) {
        if (remote.health <= 0) continue;
        if (remote.team === localTeam) localAliveAtEnd++;
        else if (remote.team !== 0) enemyAliveAtEnd++;
      }
      ctx.roundScore.showRoundEnd(winner, event.data.reason);
      ctx.roundNum = event.data.roundNumber ?? ctx.roundNum + 1;
      ctx.roundScore.update(ctx.tScore, ctx.ctScore, ctx.roundNum, 30);

      // Music: play win/loss sting based on local player's team
      if (localTeam === winnerTeam) {
        musicSystem.playRoundEndWin();
      } else {
        musicSystem.playRoundEndLoss();
      }
      const sting = pickRoundEndSting({
        winnerTeam,
        localTeam,
        localPlayerRoundKills,
        localTeamStartedEco: localTeamStartedEcoThisRound,
        localAliveAtEnd,
        enemyAliveAtEnd,
        localScoreBeforeRound,
        enemyScoreBeforeRound,
      });
      if (sting) audioManager.play(sting);

      // A-H-002 (audit 13may): "Match point" hadn't been fired anywhere. Fire it
      // when this round leaves the winning team one round from clinching, i.e.
      // their about-to-be-incremented score equals MR12_MATCH_POINT (12 = 13-1).
      // The roundEnd handler doesn't mutate ctx.{t,ct}Score (snapshot drives it),
      // so compute the prospective new score inline. We announce BEFORE the
      // round-winner line so playback ordering reads "Terrorists Win… Match
      // point" rather than the reverse.
      const MR12_MATCH_POINT = 12;
      const newWinnerScore = (winnerTeam === 1 ? ctx.tScore : ctx.ctScore) + 1;
      if (newWinnerScore === MR12_MATCH_POINT) {
        announcer.announce(AnnouncerEvent.MatchPoint);
      }

      // Announcer: call round winner
      announcer.announce(winnerTeam === 2 ? AnnouncerEvent.CTWin : AnnouncerEvent.TWin);
      break;
    }
    case 'team_change': {
      const { playerId, team } = event.data;
      if (playerId === ctx.localPlayer.id) {
        ctx.localPlayer.team = team;
        ctx.buyMenu.setTeam(team === 1 ? 'T' : 'CT');
        // Mirror the server's per-team Respawn() reset on the slot table so
        // pressing 2 routes to the new team's default pistol instead of the
        // stale prior-team slot. Snapshot path also seeds this on first
        // transition; this branch covers manual team_select mid-match and
        // auto-balance moves where the snapshot may already be in flight.
        resetLoadoutForTeam(ctx.loadoutSlots, team);
        // Reset money to starting amount on team change
        ctx.money = 800;
        updateMoneyDisplay(ctx);
      }
      break;
    }
    case 'team_full': {
      const { message } = event.data;
      ctx.teamSelectMenu.showNotification(message ?? 'Team is full');
      break;
    }

    // ── Grenade events ──

    case 'grenade_throw': {
      try {
        const { grenadeId, grenadeType, position, velocity } = event.data;
        if (!position) break;
        const pos = new THREE.Vector3(position.x, position.y, position.z);
        const vel = velocity
          ? new THREE.Vector3(velocity.x, velocity.y, velocity.z)
          : new THREE.Vector3(0, 5, 0);
        ctx.grenadeProjectiles.spawnProjectile(grenadeId, grenadeType, pos, vel, ctx.scene);
      } catch { /* malformed event */ }
      break;
    }

    case 'grenade_detonate': {
      try {
        const { grenadeId, grenadeType, position } = event.data;
        if (!position) break;
        const detonatePos = ctx.grenadeProjectiles.getPosition(grenadeId)
          ?? new THREE.Vector3(position.x, position.y, position.z);
        ctx.grenadeProjectiles.removeProjectile(grenadeId, ctx.scene);

        switch (grenadeType as string) {
          case 'he_grenade':
            spawnHEExplosion(detonatePos, ctx.scene);
            audioManager.playAt('he_explode', detonatePos, 0.6);
            break;
          case 'smoke_grenade': {
            const smokeHandle = spawnSmokeCloud(detonatePos, ctx.scene);
            ctx.activeSmokeZones.push(smokeHandle);
            audioManager.playAt('smoke_pop', detonatePos, 0.3);
            audioManager.playAt('smoke_hiss', detonatePos, 0.08);
            // Clear any existing timers for this grenade to prevent leak on repeated events
            const smokeGrenadeId = grenadeId as string ?? `smoke_${Date.now()}`;
            const existingSmokeTimers = smokeTimers.get(smokeGrenadeId);
            if (existingSmokeTimers) {
              for (const tid of existingSmokeTimers) { clearTimeout(tid); activeTimers.delete(tid); }
            }
            const smokeTimerList: ReturnType<typeof setTimeout>[] = [];
            { const t = setTimeout(() => { activeTimers.delete(t); audioManager.stop('smoke_hiss'); }, 18000); activeTimers.add(t); smokeTimerList.push(t); }
            smokeTimers.set(smokeGrenadeId, smokeTimerList);
            break;
          }
          case 'molotov':
          case 'incendiary': {
            const fireHandle = spawnFireZone(detonatePos, FIRE_RADIUS, ctx.scene);
            ctx.activeFireZones.push(fireHandle);
            audioManager.playAt('fire_ignite', detonatePos, 0.4);
            audioManager.playAt('fire_crackle', detonatePos, 0.15);
            // Track fire timer per grenade to prevent leak on repeated events
            const fireGrenadeId = grenadeId as string ?? `fire_${Date.now()}`;
            const existingFireTimers = smokeTimers.get(fireGrenadeId);
            if (existingFireTimers) {
              for (const tid of existingFireTimers) { clearTimeout(tid); activeTimers.delete(tid); }
            }
            const fireTimerList: ReturnType<typeof setTimeout>[] = [];
            { const t = setTimeout(() => { activeTimers.delete(t); audioManager.stop('fire_crackle'); }, 7000); activeTimers.add(t); fireTimerList.push(t); }
            smokeTimers.set(fireGrenadeId, fireTimerList);
            break;
          }
          case 'flashbang':
            triggerScreenShake(0.02, 0.1);
            audioManager.playAt('flash_explode', detonatePos, 0.5);
            break;
          case 'decoy_grenade': {
            const decoyHandle = createDecoyEffect(detonatePos, ctx.scene, (pos) => {
              audioManager.playAt('he_explode', pos, 0.15);
            });
            void decoyHandle;
            break;
          }
        }
      } catch { /* malformed event */ }
      break;
    }

    case 'flash_blind': {
      // Server-authoritative flash — apply white screen overlay with computed duration
      const { duration } = event.data;
      const blindSeconds = typeof duration === 'number' ? duration : 1.5;
      // Cap flash duration at 3.0s max, keep existing timer if longer
      ctx.flashBlindTimer = Math.min(Math.max(ctx.flashBlindTimer, blindSeconds), 3.0);
      applyFlashEffect(blindSeconds);

      // Tinnitus ringing effect
      audioManager.play('flash_tinnitus', 0.3);

      // Muffle other game audio while flashed (avoid cascading from repeated flashes)
      if (!flashMuffleActive) {
        flashMuffleActive = true;
        const prevMaster = audioManager.getMasterVolume();
        audioManager.setMasterVolume(prevMaster * 0.1);
        const t = setTimeout(() => {
          activeTimers.delete(t);
          audioManager.setMasterVolume(prevMaster);
          flashMuffleActive = false;
        }, 2500);
        activeTimers.add(t);
      }
      break;
    }

    case 'fire_damage': {
      // Taking fire damage from molotov/incendiary — show damage indicator
      if (event.data.victim === ctx.localPlayer.id) {
        triggerScreenShake(0.02, 0.1);
        // Directional indicator toward fire source
        if (event.data.position && ctx.directionalIndicator) {
          const dx = event.data.position.x - ctx.localPlayer.position.x;
          const dz = event.data.position.z - ctx.localPlayer.position.z;
          const worldAngle = Math.atan2(dx, -dz);
          const relativeAngle = worldAngle - ctx.localPlayer.yaw;
          ctx.directionalIndicator.showDamage(relativeAngle);
        }
      }
      break;
    }

    // ── Bomb events ──

    case 'bomb_planted': {
      // Place 3D bomb model at plant position
      const { x, z, site } = event.data;
      if (x !== undefined && z !== undefined) {
        ctx.bombEntity.setPlanted(new THREE.Vector3(x, 0, z));
      }
      // Immediately start the bomb HUD timer so it doesn't wait for next snapshot
      const plantSite = site === 'A' || site === 'B' ? site : null;
      const bombHudState: BombState = {
        planted: true,
        site: plantSite,
        timeRemaining: BOMB_TIMER_S,
        totalTime: BOMB_TIMER_S,
        carrierPlayerId: null,
        isLocalCarrier: false,
      };
      updateBombState(bombHudState);
      ctx.bombEntity.setTimerState(BOMB_TIMER_S, BOMB_TIMER_S);
      playBombPlant();
      musicSystem.updateAdaptiveState({
        bombTimeRemaining: BOMB_TIMER_S,
        bombTotalTime: BOMB_TIMER_S,
        defuseActive: false,
      });
      musicSystem.playBombPlantedTension();
      announcer.announce(AnnouncerEvent.BombPlanted);
      // M1: Bomb plant in kill feed
      ctx.hud.addEventFeedEntry('💣', `Bomb planted at ${plantSite ?? 'site'}`, '#ff4444');
      break;
    }

    case 'bomb_defused': {
      // Hide 3D bomb, play defuse sound
      ctx.bombEntity.group.visible = false;
      playBombDefused();
      musicSystem.updateAdaptiveState({ bombTimeRemaining: 0, defuseActive: false });
      musicSystem.stopBombTension();
      announcer.announce(AnnouncerEvent.BombDefused);
      // M1: Bomb defuse in kill feed
      ctx.hud.addEventFeedEntry('🛡', 'Bomb has been defused', '#44cc88');
      break;
    }

    case 'bomb_exploded': {
      // Explosion feedback — screen shake + sound, hide bomb
      ctx.bombEntity.group.visible = false;
      playBombExplode();
      musicSystem.updateAdaptiveState({ bombTimeRemaining: 0, defuseActive: false });
      musicSystem.stopBombTension();
      triggerScreenShake(0.08, 0.5);
      // M1: Bomb explode in kill feed
      ctx.hud.addEventFeedEntry('💥', 'Bomb has exploded', '#ff6600');

      // Spawn HE-style explosion at bomb position if available
      const { x: bx, z: bz } = event.data;
      if (bx !== undefined && bz !== undefined) {
        spawnHEExplosion(new THREE.Vector3(bx, 0.5, bz), ctx.scene);
      }
      break;
    }

    // ── Match / Round lifecycle events ──

    case 'site_captured': {
      // AUD-I2: server emits this on bomb plant (T captures) and bomb defuse
      // (CT recaptures). Dispatch the announcer line based on local team
      // alignment — captors hear "Site captured", losers hear "Site lost".
      const captorTeam = event.data?.team;
      const localTeam = ctx.localPlayer?.team ?? 0;
      if (typeof captorTeam !== 'number' || localTeam === 0) break;
      if (captorTeam === localTeam) {
        announcer.announce(AnnouncerEvent.Site_Taken);
      } else {
        announcer.announce(AnnouncerEvent.Site_Lost);
      }
      break;
    }

    case 'round_start': {
      // Clear previous round's ragdolls and weapon drops
      ctx.ragdollRenderer?.clear();
      ctx.weaponDropMgr?.clear();
      // Reset round kill tracking for announcer
      roundKillCount = 0;
      localPlayerRoundKills = 0;
      // F-UI-003: Reset kill streak banner state at the start of each round.
      ctx.killEffect?.resetStreak();
      // AUD-I2: reset round-scoped announcer trackers so each new round can
      // fire Countdown_1min/30sec, Clutch, and EcoRound at most once.
      lastRoundTimeRemaining = null;
      clutchAnnouncedThisRound = false;
      ecoAnnouncedThisRound = false;
      localTeamStartedEcoThisRound = ctx.money < ECO_MONEY_THRESHOLD;

      const { roundNumber } = event.data;
      if (roundNumber !== undefined) ctx.roundNum = roundNumber;
      ctx.roundPhase = 'freeze_time';
      ctx.roundScore.setPhase('freeze_time');
      ctx.roundScore.update(ctx.tScore, ctx.ctScore, ctx.roundNum, 30);
      ctx.buyMenu.setBuyAllowed(true);
      // Server resets OwnedWeapons each round — match it on the client so
      // the slot keys don't reference last round's bought primary.
      resetLoadoutForTeam(ctx.loadoutSlots, ctx.localPlayer.team);
      audioManager.play('round_start');
      musicSystem.playRoundStart();

      // AUD-I2: Eco-round announcement — fire once per round when the local
      // player starts the round under the eco-money threshold. ctx.money is
      // synced from the prior snapshot and the freshly-started round leaves
      // the previous balance intact (server only resets on team change).
      if (!ecoAnnouncedThisRound && ctx.money < ECO_MONEY_THRESHOLD) {
        announcer.announce(AnnouncerEvent.EcoRound);
        ecoAnnouncedThisRound = true;
      }
      musicSystem.updateAdaptiveState({
        bombTimeRemaining: BOMB_TIMER_S,
        bombTotalTime: BOMB_TIMER_S,
        defuseActive: false,
        recentKill: false,
        localTeamEconomy: ctx.money,
      });

      break;
    }

    case 'match_end': {
      const { winnerTeam, reason, tScore, ctScore, players } = event.data;
      const winner = winnerTeam === 1 ? 'T' : 'CT';
      ctx.roundScore.showRoundEnd(winner, reason ?? 'Match over');

      // Show end-of-match screen with full stats
      if (players && Array.isArray(players)) {
        const matchData: MatchData = {
          winnerTeam: winner,
          tScore: tScore ?? ctx.tScore,
          ctScore: ctScore ?? ctx.ctScore,
          players: players.map((p: any) => ({
            name: p.name ?? `Player ${p.id}`,
            team: (p.team === 1 ? 'T' : 'CT') as 'T' | 'CT',
            kills: p.kills ?? 0,
            deaths: p.deaths ?? 0,
            assists: 0,
            damage: p.damage ?? 0,
            moneySpent: p.money ?? 0,
            isLocal: p.id === ctx.localPlayer.id,
          })),
        };
        showEndScreen(matchData);
      }

      // MVP music fanfare
      musicSystem.playMVP();
      break;
    }

    // W-C-001 (audit 13may): server emits this as a follow-up to match_end
    // once `onMatchEnd` has run AwardMatchXP for every account. Payload is
    // { perPlayer: { "<playerId>": { gained, total, level, newLevel? } } }.
    // The match_end event is already gone by the time progression finishes,
    // so we drive the panel from this separate event keyed by local id.
    case 'match_xp': {
      if (!ctx.progressionPanel) break;
      const perPlayer = event.data?.perPlayer as
        | Record<string, { gained?: number; total?: number; level?: number; newLevel?: number }>
        | undefined;
      if (!perPlayer) break;
      const entry = perPlayer[String(ctx.localPlayer.id)];
      if (!entry) break;
      ctx.progressionPanel.show({
        xpGained: entry.gained ?? 0,
        totalXp: entry.total ?? 0,
        level: entry.level ?? 1,
        newLevel: entry.newLevel,
      });
      break;
    }

    // W-C-002 (audit 13may): server emits `match_cancelled` from the reconnect-
    // timeout cleanup goroutine when a competitive match drops below
    // MinPlayersPerTeam. Without a client handler the screen freezes in the
    // 'playing' state. Surface a toast + chat note, then disconnect — the
    // existing `connection.onDisconnect` wiring in game.ts transitions back to
    // the main menu and tears down the in-match UI.
    case 'match_cancelled': {
      const reason = typeof event.data?.reason === 'string' ? event.data.reason : 'roster fell below the minimum';
      ctx.chatUI.addMessage({
        playerId: 0,
        playerName: 'SERVER',
        channel: 'all',
        message: `Match cancelled — ${reason}`,
        timestamp: Date.now(),
      });
      ctx.hud.addEventFeedEntry('🚫', 'Match cancelled — returning to menu', '#ff6666');
      try {
        ctx.connection.disconnect?.();
      } catch {
        /* connection already closed */
      }
      break;
    }

    // ── Previously unhandled server events (C5 fix) ──

    case 'bomb_plant_start':
      showPlantProgress(event.data.duration ?? 3.2);
      break;
    case 'bomb_defuse_start':
      showDefuseProgress(event.data.duration ?? 10.0, event.data.hasKit ?? false);
      musicSystem.updateAdaptiveState({ defuseActive: true });
      break;
    case 'bomb_plant_cancel':
    case 'bomb_defuse_cancel':
      hideProgress();
      if (event.type === 'bomb_defuse_cancel') musicSystem.updateAdaptiveState({ defuseActive: false });
      break;
    case 'timeout_start': {
      const team = event.data.team ?? 'Unknown';
      ctx.chatUI.addMessage({
        playerId: 0,
        playerName: 'SERVER',
        channel: 'all',
        message: `Timeout called by ${team}`,
        timestamp: Date.now(),
      });
      // M1: Timeout in kill feed
      ctx.hud.addEventFeedEntry('⏸', `Timeout — ${team}`, '#ffcc00');
      break;
    }
    case 'timeout_end':
      ctx.chatUI.addMessage({
        playerId: 0,
        playerName: 'SERVER',
        channel: 'all',
        message: 'Timeout ended',
        timestamp: Date.now(),
      });
      ctx.hud.addEventFeedEntry('▶', 'Timeout ended', '#88cc88');
      break;
    case 'half_time':
      ctx.roundScore.showRoundEnd('', 'HALF TIME — SWITCHING SIDES');
      announcer.announce(AnnouncerEvent.HalfTime);
      ctx.chatUI.addMessage({
        playerId: 0,
        playerName: 'SERVER',
        channel: 'all',
        message: 'Half time — switching sides',
        timestamp: Date.now(),
      });
      // M1: Half time in kill feed
      ctx.hud.addEventFeedEntry('🔄', 'Half Time — Switching Sides', '#ffcc00');
      break;
    case 'overtime_start': {
      const period = event.data.period ?? 1;
      ctx.roundScore.showRoundEnd('', `OVERTIME — PERIOD ${period}`);
      announcer.announce(AnnouncerEvent.OvertimeStart);
      ctx.chatUI.addMessage({
        playerId: 0,
        playerName: 'SERVER',
        channel: 'all',
        message: `Overtime period ${period} starting`,
        timestamp: Date.now(),
      });
      // M1: Overtime in kill feed
      ctx.hud.addEventFeedEntry('⚡', `Overtime — Period ${period}`, '#ff8800');
      break;
    }
    case 'player_abandoned': {
      const abandonName = getPlayerName(ctx, event.data.playerId);
      ctx.chatUI.addMessage({
        playerId: 0,
        playerName: 'SERVER',
        channel: 'all',
        message: `${abandonName} has abandoned the match`,
        timestamp: Date.now(),
      });
      break;
    }
    case 'player_kicked': {
      const kickedName = getPlayerName(ctx, event.data.playerId);
      ctx.chatUI.addMessage({
        playerId: 0,
        playerName: 'SERVER',
        channel: 'all',
        message: `${kickedName} has been kicked`,
        timestamp: Date.now(),
      });
      break;
    }
    case 'fall_damage': {
      // GP-W2: Surface fall damage with damage number, pain SFX, and a
      // proportionally larger camera shake (legacy was a near-imperceptible
      // 0.01/0.08s twitch).
      if (event.data.playerId === ctx.localPlayer.id) {
        const damage = typeof event.data.damage === 'number' ? event.data.damage : 0;
        if (damage > 0 && ctx.damageNumbers) {
          // Spawn the damage number above the local player's head so the
          // sprite pops out where the player is looking, mirroring the
          // remote-player damage path.
          const head = ctx.localPlayer.position
            .clone()
            .setY(ctx.localPlayer.position.y + 1.8);
          ctx.damageNumbers.spawn(head, damage, false);
        }
        playPainGrunt();
        // Scale shake intensity with damage (capped) so a 5HP scrape barely
        // jiggles but a 60HP near-death drop hits hard.
        const shake = Math.min(0.02 + damage * 0.0008, 0.08);
        triggerScreenShake(shake, 0.25);
      }
      break;
    }
    case 'vote_kick_started': {
      const voteTarget = getPlayerName(ctx, event.data.targetId);
      const voteInitiator = getPlayerName(ctx, event.data.initiatorId);
      ctx.chatUI.addMessage({
        playerId: 0,
        playerName: 'SERVER',
        channel: 'all',
        message: `Vote kick started by ${voteInitiator} against ${voteTarget}`,
        timestamp: Date.now(),
      });
      // C2: Show vote panel with countdown + accept/decline UI
      if (ctx.votePanel) {
        ctx.votePanel.show({
          initiator: voteInitiator,
          target: voteTarget,
          duration: event.data.duration ?? 30,
        });
      }
      // M1: Vote kick in kill feed
      ctx.hud.addEventFeedEntry('🗳', `Vote to kick ${voteTarget}`, '#ffcc00');
      break;
    }
    // C2: Vote tally updates (sent as server broadcasts vote progress)
    case 'vote_kick_update':
      if (ctx.votePanel) {
        ctx.votePanel.updateVotes(event.data.yes ?? 0, event.data.no ?? 0);
      }
      break;
    case 'vote_kick_ended':
      if (ctx.votePanel) {
        // Record the outcome into the vote-history strip (batch 13) before
        // hiding the panel. Server emits `result: "accepted" | "rejected" |
        // "expired"`; treat accepted as passed, anything else as failed.
        const voteResult = event.data?.result;
        const passed = voteResult === 'accepted';
        ctx.votePanel.recordVoteResult?.(passed);
        ctx.votePanel.hide();
      }
      break;

    case 'buy': {
      // Authoritative confirmation that the local player owns this weapon —
      // route it to its slot so keybinds 1/2/3 select it.
      if (event.data?.playerId === ctx.localPlayer.id) {
        const wid = event.data.weaponId as WeaponId | undefined;
        if (wid && WEAPONS[wid]) setLoadoutSlot(ctx.loadoutSlots, wid);
      }
      break;
    }
    case 'buy_equipment':
    case 'buy_grenade':
      break;

    case 'weapon_drop': {
      if (ctx.weaponDropMgr) {
        const { id, weaponId, x, y, z } = event.data;
        ctx.weaponDropMgr.addDrop(id, weaponId, x, y ?? 0, z);
      }
      break;
    }

    case 'weapon_pickup': {
      if (ctx.weaponDropMgr) {
        const { dropId, playerId, weaponId } = event.data;
        ctx.weaponDropMgr.removeDrop(dropId);
        if (playerId === ctx.localPlayer.id && weaponId) {
          const wid = weaponId as WeaponId;
          if (WEAPONS[wid]) {
            ctx.localPlayer.equipWeapon(wid);
            ctx.hud.updateWeaponName(WEAPONS[wid].name);
            setLoadoutSlot(ctx.loadoutSlots, wid);
          }
        }
      }
      break;
    }

    case 'arms_race_advance': {
      const { playerId, weaponId } = event.data;
      const name = getPlayerName(ctx, playerId);
      ctx.hud.addKillFeedEntry(name, '', weaponId, false, false);
      // Server's Arms Race ladder advances the killer's WeaponID; mirror it
      // into the slot table so pressing slot-1 keeps the new ladder weapon.
      if (playerId === ctx.localPlayer.id) {
        const wid = weaponId as WeaponId | undefined;
        if (wid && WEAPONS[wid]) setLoadoutSlot(ctx.loadoutSlots, wid);
      }
      break;
    }

    // W-H-002 (audit 13may): knife humiliation in Arms Race demotes the victim
    // one rung. Server already mutates server-side weapon; surface a toast so
    // the player understands why their slot-1 weapon just regressed.
    case 'arms_race_demote': {
      const { playerId, weaponId } = event.data;
      const wid = weaponId as WeaponId | undefined;
      const weaponName = (wid && WEAPONS[wid]?.name) || weaponId || 'previous weapon';
      if (playerId === ctx.localPlayer.id) {
        ctx.hud.addEventFeedEntry('⬇', `DEMOTED → ${weaponName}`, '#ff8866');
        if (wid && WEAPONS[wid]) setLoadoutSlot(ctx.loadoutSlots, wid);
      } else {
        const name = getPlayerName(ctx, playerId);
        ctx.hud.addEventFeedEntry('⬇', `${name} demoted → ${weaponName}`, '#ffaa88');
      }
      break;
    }

    // W-H-001 (audit 13may): Deathmatch bonus kill (extra +1 score) — flash a
    // toast so the bonus is visible. `weaponId` is the rotation weapon used.
    case 'dm_bonus_kill': {
      const { killer, weaponId } = event.data;
      const wid = weaponId as WeaponId | undefined;
      const weaponName = (wid && WEAPONS[wid]?.name) || weaponId || 'bonus weapon';
      if (killer === ctx.localPlayer.id) {
        ctx.hud.addEventFeedEntry('⭐', `BONUS KILL (+1) — ${weaponName}`, '#ffdd55');
      } else {
        const name = getPlayerName(ctx, killer);
        ctx.hud.addEventFeedEntry('⭐', `${name} bonus kill — ${weaponName}`, '#ffcc44');
      }
      break;
    }

    // W-H-001 (audit 13may): DM bonus weapon rotation — the slot the server
    // doubles credit on changed. Show a toast so players know which weapon is
    // currently scoring +1.
    case 'dm_bonus_weapon': {
      const { weaponId, secondsLeft } = event.data;
      const wid = weaponId as WeaponId | undefined;
      const weaponName = (wid && WEAPONS[wid]?.name) || weaponId || 'next weapon';
      const seconds = typeof secondsLeft === 'number' ? Math.max(0, Math.round(secondsLeft)) : null;
      const suffix = seconds !== null ? ` (${seconds}s)` : '';
      ctx.hud.addEventFeedEntry('🎯', `Bonus weapon: ${weaponName}${suffix}`, '#ffaa44');
      break;
    }

    // ── Surrender events ──
    case 'surrender_vote_started': {
      // W-H-003 (audit 13may): teammates need a vote prompt, not just a log line.
      // Reuse VotePanel (also used for vote-kick above) with a surrender-specific
      // title + per-show onVote routing to `surrender_vote`. Server's
      // SurrenderVoteTimeout is 30s; fall back to it when the event omits one.
      if (ctx.votePanel) {
        ctx.votePanel.show({
          title: 'Surrender vote',
          duration: event.data.duration ?? 30,
          onVote: (yes: boolean) =>
            ctx.connection.send({ type: 'surrender_vote', data: { vote: yes } }),
        });
      }
      break;
    }
    case 'surrender_accepted': {
      // Record passed outcome on the surrender VotePanel history strip
      // (batch 13). The panel is shared with vote-kick so we use the same
      // recordVoteResult call.
      ctx.votePanel?.recordVoteResult?.(true);
      break;
    }
    case 'surrender_rejected':
      ctx.votePanel?.recordVoteResult?.(false);
      break;

    // ── Environment events ──
    case 'water_splash': {
      // SRV-W2: server emits this on the rising edge of in-water entry per
      // player (ticker_physics.go). Play a positional splash SFX so nearby
      // players + the local player hear the entry.
      const x = typeof event.data?.x === 'number' ? event.data.x : null;
      const y = typeof event.data?.y === 'number' ? event.data.y : null;
      const z = typeof event.data?.z === 'number' ? event.data.z : null;
      if (x !== null && y !== null && z !== null) {
        playWaterSplash({ x, y, z });
      }
      break;
    }
    case 'decoy_gunfire': {
      // GP-W1: server emits this every ~0.5s for active decoys. Play the
      // gunshot positionally so enemies hear it from the decoy's location
      // rather than as a flat 2D blip from the listener.
      const pos = event.data?.position;
      if (pos && typeof pos.x === 'number' && typeof pos.y === 'number' && typeof pos.z === 'number') {
        playDecoyGunfire({ x: pos.x, y: pos.y, z: pos.z });
      } else {
        // Fallback: server might omit position (legacy event payload) — use
        // the existing 2D distant gunfire so the cue still plays.
        audioManager.play('distant_gunfire');
      }
      break;
    }
    case 'door_toggle':
      // Door state change handled by interactive element system
      break;
    case 'respawn': {
      const { playerId } = event.data;
      if (playerId === ctx.localPlayer.id) {
        ctx.state = 'playing';
        ctx.deathScreen.hide();
        if (ctx.xrayOverlay?.isActive) ctx.xrayOverlay.toggle();
        resetAimpunch();
        // Match server's Respawn() — wipe slot table back to defaults so
        // last life's bought primary doesn't linger.
        resetLoadoutForTeam(ctx.loadoutSlots, ctx.localPlayer.team);
      }
      break;
    }

    default:
      break;
  }
}

function getPlayerName(ctx: GameContext, id: number): string {
  if (id === ctx.localPlayer.id) return ctx.localPlayer.name;
  const remote = ctx.remotePlayers.get(id);
  return remote?.name ?? `Player ${id}`;
}
